import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AcceptedPegoutQuote,
  FlyoverError,
  FlyoverUtils,
  PegoutPaymentData,
  PegoutQuote,
} from '@rsksmart/flyover-sdk';
import { Box } from '@mui/material';
import { Path, MetamaskError } from '../types';
import { assertIsQuote } from '../types/assertions';
import SectionContainer from '../components/UI/SectionContainer';
import SectionPaper from '../components/UI/SectionPaper';
import InfoCard from '../components/UI/InfoCard';
import FlyButton from '../components/UI/FlyButton';
import Segue from '../components/UI/Segue';
import JsonPaper from '../components/UI/JsonPaper';
import FlyError from '../components/UI/FlyError';
import QrCode from '../components/UI/QrCode';
import useStore from '../hooks/useStore';
import flyover from '../flyover';
import { buildPegoutQrPayload } from '../utils/paymentUri';
import { ethers } from '@rsksmart/bridges-core-sdk';
import { roundUp } from './Connect';

export interface LocationState {
  txHash?: string;
}

function PaymentPegOut() {
  const navigate = useNavigate();
  const {
    flyover: { quoteReturn, acceptedQuote: storeAcceptedQuote },
    ui: { isDevMode },
    web3: { chainId },
  } = useStore();

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [paymentData, setPaymentData] = useState<PegoutPaymentData | null>(
    null,
  );

  const quote = quoteReturn[0] as PegoutQuote | undefined;
  const acceptedQuote = storeAcceptedQuote as AcceptedPegoutQuote | undefined;
  const depositAmount = quote ? FlyoverUtils.getQuoteTotal(quote) : BigInt(0);
  const resolvedChainId = chainId ?? quote?.quote?.chainId;

  useEffect(() => {
    if (!quote || !acceptedQuote) return;
    (async () => {
      try {
        const paymentData = await flyover.getPegoutPaymentData(quote, acceptedQuote);
        setPaymentData(paymentData);
      } catch (error) {
        setErrorMessage(
          error instanceof Error
            ? `PaymentPegOut: ${error.message}`
            : 'PaymentPegOut: Cannot get pegout payment data',
        );
      }
    })();
  }, [quote, acceptedQuote]);

  const qrPayload =
    paymentData && resolvedChainId
      ? buildPegoutQrPayload(paymentData, resolvedChainId)
      : '';

  const handlePayment = async () => {
    setLoading(true);
    try {
      assertIsQuote(quote);
      if (!acceptedQuote || !acceptedQuote.signature) {
        throw new Error('No accepted quote found. Please accept the quote first.');
      }
      const txHash = await flyover.depositPegout(
        quote,
        acceptedQuote.signature,
        depositAmount,
      );
      const state: LocationState = { txHash };
      navigate(Path.deposit, { state });
    } catch (error) {
      if (error instanceof FlyoverError) {
        const errorDetails: MetamaskError = error.details;
        const metamaskErrMsg = errorDetails.error ?? '';
        // don't show error message if user has cancelled a tx himself
        if (!/user rejected transaction/i.test(metamaskErrMsg))
          setErrorMessage(`PaymentPegOut: ${error.message}`);
      } else if (error instanceof Error) {
        setErrorMessage(`PaymentPegOut: ${error.message}`);
      } else {
        setErrorMessage('PaymentPegOut: Cannot process payment');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SectionPaper heading="Pay for quote">
        {quote && acceptedQuote && (
          <Box
            sx={{
              display: 'flex',
              gap: '2rem',
              flexDirection: isDevMode ? 'column' : 'row',
              justifyContent: 'center',
              '@media (max-width: 1000px)': {
                flexDirection: 'row',
              },
              alignItems: isDevMode ? 'center' : 'flex-start',
              '@media (max-width: 800px)': {
                flexDirection: 'column',
                alignItems: 'center',
              },
            }}
          >
            {qrPayload && <QrCode payload={qrPayload} />}
            <SectionContainer spacing={2} sx={{ margin: 'unset' }}>
              <InfoCard
                title="Amount to deposit (RBTC) = Amount to transfer + Call Fee"
                message={roundUp(
                  ethers.utils.formatEther(depositAmount),
                ).toString()}
                hasCopy
              />
              <InfoCard
                title="Number of block confirmations"
                message={quote?.quote?.depositConfirmations?.toString() ?? '0'}
                hasCopy
              />
              <InfoCard title="Quote Hash" message={quote?.quoteHash} hasCopy />
              <FlyButton onClick={handlePayment}>Pay & Continue</FlyButton>
            </SectionContainer>
          </Box>
        )}
      </SectionPaper>
      {isDevMode && (
        <SectionPaper heading="Dev Info">
          {acceptedQuote && (
            <SectionContainer spacing={2}>
              <JsonPaper title="Accepted PegOut quote" value={acceptedQuote} />
              {quote && <JsonPaper title="PegOut quote" value={quote} />}
            </SectionContainer>
          )}
        </SectionPaper>
      )}
      <FlyError errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
      <Segue open={loading} />
    </>
  );
}

export default PaymentPegOut;
